import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Share2, MoreHorizontal, X, ExternalLink, ArrowUp, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAgent } from '../../../../context/agent/AgentContext';
import { useVoucherLock } from '../../../../components/gating/PlanGate';
import PDFVoucherView from '../Shared/PDFVoucherView';
import EmptyStateTable from '../../../../components/feedback/EmptyStateTable';
import vouchersData from '../../../../data/vouchers/vouchersData.json';

const sampleVoucherData = vouchersData.sampleVoucher || {};
const voucherGhostRows = vouchersData.voucherEmptyState?.ghostRows || [];
const voucherColumns = vouchersData.voucherEmptyState?.columns || [];

const VoucherEmptyState = () => {
    const navigate = useNavigate();
    const { agent, voucherTheme } = useAgent();
    const { isLocked: isVoucherLocked } = useVoucherLock();
    const [showSampleActions, setShowSampleActions] = useState(false);
    const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);

    // Lock body scroll when sample modal is open
    useEffect(() => {
        if (isSampleModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isSampleModalOpen]);

    const companyName = agent?.company_name || 'ACS Tours & Travels';

    const handleFileDrop = (files) => {
        if (isVoucherLocked) {
            navigate('/subscription');
            toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
            return;
        }
        const validFile = files.find(f => f.type === 'application/pdf' || f.type.startsWith('image/'));
        if (!validFile) {
            toast.error('Please upload a PDF or image file (JPG, PNG).');
            return;
        }
        toast.success(`Loaded "${validFile.name}". Starting voucher creation...`);
        navigate('/create-voucher', {
            state: {
                preloadedHotelFile: validFile
            }
        });
    };

    const renderRow = (row) => {
        if (row.isSample) {
            return (
                <div className="hidden md:grid grid-cols-12 gap-4 items-center px-6 py-3 bg-white border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <div className="col-span-3">
                        <div className="text-slate-800 text-sm leading-tight font-normal">{row.name}</div>
                        <div className="text-[11px] text-slate-400 font-normal mt-0.5">{row.subtitle}</div>
                    </div>
                    <div className="col-span-2">
                        <span className="inline-block px-2.5 py-0.5 bg-slate-50 border border-slate-200/80 rounded-lg font-mono text-xs font-normal text-slate-700 tracking-wider">
                            {row.code}
                        </span>
                    </div>
                    <div className="col-span-2 text-sm font-normal text-slate-700">{row.destination}</div>
                    <div className="col-span-2 text-sm text-slate-600 font-normal">{row.dates}</div>
                    <div className="col-span-1">
                        <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#067978] text-white text-[11px] font-normal tracking-wide shadow-2xs">
                            {row.statusText}
                        </span>
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowSampleActions(!showSampleActions)}
                                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 border border-slate-200/80 transition-all cursor-pointer"
                                title="More options"
                            >
                                <MoreHorizontal size={14} />
                            </button>
                            {showSampleActions && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowSampleActions(false)} />
                                    <div className="absolute right-0 top-full mt-1.5 w-44 bg-white border border-slate-200 rounded-2xl shadow-xl p-1.5 z-50">
                                        <button
                                            onClick={() => {
                                                setIsSampleModalOpen(true);
                                                setShowSampleActions(false);
                                            }}
                                            className="w-full text-left px-3 py-2 text-xs font-normal text-slate-700 hover:bg-[#067978]/5 hover:text-[#067978] rounded-xl flex items-center gap-2"
                                        >
                                            <ExternalLink size={13} className="text-[#067978]" />
                                            <span>View Sample PDF</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={`hidden md:grid grid-cols-12 gap-4 items-center px-6 py-3 bg-white border-b border-slate-100 ${row.opacity || 'opacity-30'} select-none pointer-events-none`}>
                <div className="col-span-3">
                    <div className="text-slate-600 text-sm leading-tight font-normal">{row.name}</div>
                    <div className="text-[11px] text-slate-400 font-normal mt-0.5">{row.subtitle}</div>
                </div>
                <div className="col-span-2">
                    <span className="inline-block px-2.5 py-0.5 bg-slate-50 border border-slate-200/80 rounded-lg font-mono text-xs font-normal text-slate-500 tracking-wider">
                        {row.code}
                    </span>
                </div>
                <div className="col-span-2 text-sm font-normal text-slate-600">{row.destination}</div>
                <div className="col-span-2 text-sm text-slate-500 font-normal">{row.dates}</div>
                <div className="col-span-1">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-normal tracking-wide ${row.statusClass}`}>
                        {row.statusText}
                    </span>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                    <div className="p-1.5 rounded-xl text-slate-400 border border-slate-200">
                        <Share2 size={14} />
                    </div>
                    <div className="px-3 py-1 rounded-xl border border-slate-200 text-xs font-normal text-slate-400">
                        Dashboard
                    </div>
                </div>
            </div>
        );
    };

    const renderMobileRow = (row) => (
        <div className={`bg-white border rounded-2xl p-3.5 ${row.isSample ? 'border-slate-200/90 shadow-xs' : 'border-slate-100 opacity-40 select-none pointer-events-none'}`}>
            <div className="flex items-center justify-between">
                <div>
                    <h4 className="text-slate-800 text-sm font-normal">{row.name}</h4>
                    <p className="text-[11px] text-slate-400 font-normal">{row.subtitle}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-normal ${row.isSample ? 'bg-[#067978] text-white shadow-2xs' : 'bg-teal-50 text-[#067978] border border-teal-200/70'}`}>
                    {row.statusText}
                </span>
            </div>
            <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-slate-100 text-xs text-slate-600 font-normal">
                <span>{row.destination} · {row.dates}</span>
                <span className="font-mono bg-slate-50 border border-slate-200 px-2 py-0.5 rounded text-[11px]">
                    {row.code}
                </span>
            </div>
        </div>
    );

    return (
        <EmptyStateTable
            title="No vouchers yet"
            description="Your list fills as you create them. Each one moves on its own from Upcoming to On-trrip to Completed — you never update a status by hand."
            primaryAction={{
                label: 'Create Your voucher',
                isLocked: isVoucherLocked,
                tooltipText: 'Subscription required to create vouchers. Upgrade to unlock.',
                onClick: () => {
                    if (isVoucherLocked) {
                        navigate('/subscription');
                        toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
                    } else {
                        navigate('/create-voucher');
                    }
                },
                icon: <Plus size={18} className="shrink-0 stroke-[2.5]" />
            }}
            secondaryAction={{
                label: 'Open the sample voucher',
                onClick: () => setIsSampleModalOpen(true)
            }}
            dropZone={{
                title: 'Drop a hotel confirmation here',
                subtitle: "PDF · JPG · PNG. That's enough to start.",
                badgeText: 'REQUIRED',
                isLocked: isVoucherLocked,
                tooltipText: 'Subscription required to create vouchers. Upgrade to unlock.',
                icon: <ArrowUp size={18} className="stroke-[2.5]" />,
                accept: '.pdf,image/png,image/jpeg,image/jpg',
                onFileDrop: handleFileDrop,
                onDropZoneClick: isVoucherLocked ? () => {
                    navigate('/subscription');
                    toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
                } : undefined
            }}
            columns={voucherColumns}
            rows={voucherGhostRows}
            renderRow={renderRow}
            renderMobileRow={renderMobileRow}
        >
            {/* Modal for Sample Preview */}
            {isSampleModalOpen && typeof document !== 'undefined' && createPortal(
                <div
                    onClick={() => setIsSampleModalOpen(false)}
                    className="fixed inset-0 z-[999999] bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5 md:p-6 overflow-y-auto"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-slate-100 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200 relative z-10 my-auto"
                    >
                        <div className="bg-white px-5 sm:px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-[#067978]/10 text-[#067978] flex items-center justify-center font-normal text-xs">
                                    PDF
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-sm sm:text-base font-normal text-slate-800">
                                            Sample Voucher Preview
                                        </h3>
                                        <span className="text-[10px] font-mono font-normal px-2 py-0.5 rounded-md bg-teal-50 text-[#067978] border border-teal-200">
                                            bysid
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 font-normal mt-0.5">
                                        Karan Verma · Jaipur, Rajasthan · 5–8 Sep 2026
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsSampleModalOpen(false)}
                                    className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
                                    aria-label="Close modal"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-slate-100 flex justify-center no-scrollbar">
                            <div className="bg-white shadow-lg rounded-2xl overflow-hidden max-w-[800px] w-full border border-slate-200/80 my-auto">
                                <PDFVoucherView
                                    data={sampleVoucherData}
                                    bookingId="bysid"
                                    logo={agent?.company_logo || agent?.logo}
                                    agentName={companyName}
                                    agentEmail={agent?.email || 'support@trrip.app'}
                                    agentPhone={agent?.phone_number || agent?.phone || '+91 98230 45678'}
                                    agentAddress={agent?.address || 'Pink City Tower, MI Road'}
                                    city={agent?.city || 'Jaipur'}
                                    pincode={agent?.pincode || '302001'}
                                    customTheme={voucherTheme}
                                />
                            </div>
                        </div>

                        <div className="bg-white px-6 py-3.5 border-t border-slate-200 flex items-center justify-between shrink-0">
                            <span className="text-xs text-slate-500 font-normal hidden sm:inline">
                                Ready to issue real vouchers for your clients?
                            </span>
                            <div className="flex items-center gap-2.5 ml-auto sm:ml-0">
                                <button
                                    type="button"
                                    onClick={() => setIsSampleModalOpen(false)}
                                    className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-normal rounded-xl transition-all cursor-pointer"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsSampleModalOpen(false);
                                        navigate('/create-voucher');
                                    }}
                                    className="px-4 py-2 bg-[#067978] hover:bg-[#056463] text-white text-xs font-normal rounded-xl shadow-xs transition-all cursor-pointer"
                                >
                                    Create Real Voucher
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </EmptyStateTable>
    );
};

export default VoucherEmptyState;
